import type { EVMAnalyticsEvent } from "./events"
import type { TVMEventPropertyKey } from "./properties"
import type { EVMAssetOutputType, EVMAssetSurface } from "./propertyEnums"

/**
 * Which layer is allowed to emit an event. Enforced statically by the
 * `@aix/analytics-event-owner` ESLint rule and again at runtime by `analytics-audit --live`
 * (via PostHog's `$lib`). A backend event appearing from posthog-js means either the
 * fire site moved or someone is spoofing — both are worth failing on.
 */
export enum EVMEventOwner {
  BACKEND = "backend",
  FRONTEND = "frontend",
  /** Legitimately emitted from both layers. Use sparingly — it disables the check. */
  BOTH = "both",
}

/** Product domain. Groups rows in the generated doc and scopes dashboard tiles. */
export enum EVMEventCategory {
  AUTH = "auth",
  ACCOUNT = "account",
  SUBSCRIPTION = "subscription",
  BILLING = "billing",
  REVOPS = "revops",
  CREDITS = "credits",
  FUNNEL = "funnel",
  ENGAGEMENT = "engagement",
  INTEGRATIONS = "integrations",
  CREATION = "creation",
  DISTRIBUTION = "distribution",
  CONCURRENCY = "concurrency",
  LIFECYCLE = "lifecycle",
  DEBUG = "debug",
}

/** The primary noun the event is about. Drives entity-level joins in analysis. */
export enum EVMEventEntity {
  USER = "user",
  TEAM = "team",
  VIDEO = "video",
  ASSET = "asset",
  STORYBOARD = "storyboard",
  SUBSCRIPTION = "subscription",
  CREDIT = "credit",
  SESSION = "session",
  NONE = "none",
}

/** Migration status. `analytics-audit` treats these very differently — see registry.ts. */
export enum EVMEventStatus {
  ACTIVE = "active",
  /** Still emitted somewhere, but no new fire sites allowed. Must set `replacedBy`. */
  DEPRECATED = "deprecated",
  /** No longer emitted anywhere. Any occurrence post-cutover is leakage. */
  RETIRED = "retired",
}

/**
 * How stale this event's most recent occurrence may get before the freshness check
 * fails. Only meaningful in `--live` mode; the offline audit ignores it.
 */
export enum EVMFreshnessSla {
  /** High-volume events. Silence for an hour means something is broken. */
  HOURLY = "hourly",
  DAILY = "daily",
  WEEKLY = "weekly",
  /** Genuinely bursty or rare (debug, cancellations). Never alerts. */
  NONE = "none",
}

/**
 * What the payload is permitted to carry. The contract's hard rule is that no event
 * ever carries prompts, raw error messages, filenames, product names or source URLs —
 * `CONTAINS_USER_CONTENT` marks the events that predate that rule and still need
 * cleaning, so the audit can report on the debt rather than pretend it does not exist.
 */
export enum EVMPrivacyClass {
  /** No user-identifying or user-authored content beyond opaque ids. */
  NO_PII = "no_pii",
  /** Carries an opaque user/team identifier but no free text. */
  PSEUDONYMOUS = "pseudonymous",
  /** Known to carry user-authored text or an email. Debt — should trend to zero. */
  CONTAINS_USER_CONTENT = "contains_user_content",
}

/**
 * The `requiredProps` list for one event.
 *
 * A handful of events (NOT_ENOUGH_CREDITS_UPSELL, PUBLIC_LINK_CREATED) carry nothing but
 * `setOnce`/`add` person-property operations, so they have no queryable event properties
 * at all and `TVMEventPropertyKey` resolves to `never`. Collapsing that case to the empty
 * tuple states the rule directly — such an event can only declare an empty required list —
 * instead of leaving an uninhabited `readonly never[]` that empty-array inference chokes on.
 */
export type TVMRequiredProps<K extends EVMAnalyticsEvent> = [TVMEventPropertyKey<K>] extends [never]
  ? readonly []
  : readonly TVMEventPropertyKey<K>[]

/** Metadata for one event. Written per-event in `registry.ts`. */
export interface IVMEventDefinition<K extends EVMAnalyticsEvent> {
  /** One line, present tense, describing when it fires. Rendered into the doc. */
  description: string
  owner: EVMEventOwner
  category: EVMEventCategory
  entity: EVMEventEntity
  status: EVMEventStatus
  privacy: EVMPrivacyClass
  freshnessSla: EVMFreshnessSla
  /**
   * Properties that MUST be present on every emission. Typed against this event's own
   * payload, so a typo or a since-renamed property is a compile error rather than a
   * dashboard that quietly filters everything out.
   */
  requiredProps: TVMRequiredProps<K>
  /** Prior names. Historical queries need these; the doc renders them as an alias table. */
  legacyAliases?: readonly string[]
  /** Required when status is DEPRECATED or RETIRED. */
  replacedBy?: EVMAnalyticsEvent
  /** ISO date the event was marked deprecated. */
  deprecatedAt?: string
  /**
   * ISO timestamp after which occurrences of a RETIRED event count as leakage.
   * Set at cutover; the live audit's legacy-leakage check keys off it.
   */
  retiredAt?: string
}

/**
 * Type-erased view of a definition, used only as the `satisfies` constraint on the
 * registry. `readonly T[]` is covariant so the per-event `requiredProps` narrows into
 * `readonly string[]` cleanly, which lets one heterogeneous object satisfy
 * `Record<EVMAnalyticsEvent, …>` and gives us exhaustiveness for free.
 */
export interface IVMEventDefinitionErased {
  description: string
  owner: EVMEventOwner
  category: EVMEventCategory
  entity: EVMEventEntity
  status: EVMEventStatus
  privacy: EVMPrivacyClass
  freshnessSla: EVMFreshnessSla
  requiredProps: readonly string[]
  legacyAliases?: readonly string[]
  replacedBy?: EVMAnalyticsEvent
  deprecatedAt?: string
  retiredAt?: string
}

/**
 * Property keys valid on EVERY step of a funnel, computed as the intersection of each
 * step's payload keys.
 *
 * This exists because the most common way a PostHog funnel silently breaks is a
 * breakdown property that only some steps carry — PostHog drops the rows without
 * complaining and the funnel reads as a conversion cliff. Making it a compile error is
 * cheaper than finding it in a board meeting.
 */
export type TVMCommonPropertyKey<TSteps extends readonly EVMAnalyticsEvent[]> = TSteps extends readonly [
  infer THead,
  ...infer TRest,
]
  ? THead extends EVMAnalyticsEvent
    ? TRest extends readonly EVMAnalyticsEvent[]
      ? TRest["length"] extends 0
        ? TVMEventPropertyKey<THead>
        : TVMEventPropertyKey<THead> & TVMCommonPropertyKey<TRest>
      : never
    : never
  : never

/** Scalar a funnel filter may compare against. String enums widen into `string`. */
export type TVMFunnelFilterValue = string | number | boolean

/** One reusable, named funnel. Dashboard tiles reference these by key via `analyticsRef`. */
export interface IVMFunnelDefinition<TSteps extends readonly EVMAnalyticsEvent[]> {
  /** Human label for the generated funnel sheet. */
  label: string
  /** Why this funnel exists / what decision it informs. */
  description: string
  /** Ordered. PostHog evaluates steps in this sequence. */
  steps: TSteps
  /** Applied to every step. Keys must exist on all steps. */
  filters?: Partial<Record<TVMCommonPropertyKey<TSteps>, TVMFunnelFilterValue>>
  /** Breakdown dimensions. Keys must exist on all steps — see TVMCommonPropertyKey. */
  breakdowns?: readonly TVMCommonPropertyKey<TSteps>[]
  /**
   * Filter/breakdown properties this funnel uses that are declared on every step but not
   * yet REQUIRED on every step — i.e. some fire site can still omit them.
   *
   * The type system proves a property is declarable; it cannot prove a given call site
   * populates it. Until it is required everywhere, PostHog will silently drop the steps
   * that omit it and the funnel under-reports. Listing it here says "we know, it is
   * tracked" rather than shipping a funnel that quietly lies. The unit tests enforce both
   * directions: an unlisted gap fails, and a listed entry that is no longer a gap also
   * fails, so the list can only shrink.
   */
  blockedOn?: readonly TVMCommonPropertyKey<TSteps>[]
  /** Conversion window in days. PostHog defaults to 14 when omitted. */
  conversionWindowDays?: number
}

/** Type-erased funnel, for the `satisfies` constraint and for generic consumers. */
export interface IVMFunnelDefinitionErased {
  label: string
  description: string
  steps: readonly EVMAnalyticsEvent[]
  filters?: Partial<Record<string, TVMFunnelFilterValue>>
  breakdowns?: readonly string[]
  blockedOn?: readonly string[]
  conversionWindowDays?: number
}

/**
 * How real a flow node is.
 *
 * Without this, the hierarchy silently mixes shipped surfaces with design intent, and
 * `flows.ts` reads as an inventory when it is actually a wish. The "every surface is
 * measured" test then passes on a funnel merely being *declared*, while the surface
 * emits nothing — which is exactly the failure the hierarchy exists to catch.
 *
 * `analytics-audit` verifies the claim against real fire sites rather than trusting it.
 */
export enum EVMFlowStatus {
  /** Code ships and the surface emits events. The audit fails if no fire site exists. */
  SHIPPED_INSTRUMENTED = "shipped_instrumented",
  /** Code ships but emits nothing. A known blind spot, deliberately recorded. */
  SHIPPED_BLIND = "shipped_blind",
  /** Designed, not built. May reference a funnel as intent; claims no measurement. */
  PLANNED = "planned",
}

/**
 * The stages every creation surface passes through.
 *
 * A surface is only fully measured when all five are emitted somewhere in its code. The
 * coverage audit reports per-surface gaps against this list, which is what turns "we have
 * some analytics" into a checklist you can finish.
 */
export enum EVMLifecycleStage {
  /** User arrives and picks something. Gives the funnel a denominator. */
  ENTRY = "entry",
  /** The request begins. Exactly one per top-level user request. */
  STARTED = "started",
  /** Terminal success — at least one asset delivered. */
  COMPLETED = "completed",
  /** Terminal failure — nothing delivered, including swept timeouts. */
  FAILED = "failed",
  /** The user takes the result out of the product. The only true value signal. */
  DELIVERED = "delivered",
}

/**
 * One node of the product flow hierarchy (the Figma tree). Leaves bind a surface to
 * the funnel that measures it; the doc generator renders the whole tree as Mermaid.
 */
export interface IVMFlowNode<TFunnelKey extends string> {
  label: string
  description?: string
  /** Required on leaves — a leaf with no status is an unaudited claim. */
  status?: EVMFlowStatus
  surface?: EVMAssetSurface
  outputType?: EVMAssetOutputType
  /**
   * Constrained to real funnel keys by the caller's instantiation. On a PLANNED node
   * this records which funnel is *intended*, not one that currently has data.
   */
  funnel?: TFunnelKey
  /** Events emitted by this node beyond those implied by its funnel. */
  events?: readonly EVMAnalyticsEvent[]
  /**
   * Repo-relative files and directories that own this surface.
   *
   * The coverage audit scans exactly these paths for each lifecycle stage, which is what
   * lets it say "the completion handler for this surface emits nothing" instead of the
   * useless "this event is not fired anywhere". Point them at the code that owns the
   * state transitions — the router, the service, the job handler — not the whole app.
   */
  codePaths?: readonly string[]
  children?: Record<string, IVMFlowNode<TFunnelKey>>
}
